import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Mail, Phone, MapPin, Send, Check } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');
    setSubmitSuccess(false);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitSuccess(true);
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      setSubmitError(t('contact.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const seoTitles = {
    it: 'Contatti | Offroad Italia - Scrivici o Chiamaci',
    en: 'Contact Us | Offroad Italia - Write or Call Us',
    de: 'Kontakt | Offroad Italia - Schreiben oder Anrufen',
    fr: 'Contact | Offroad Italia - Écrivez ou Appelez-nous',
    es: 'Contacto | Offroad Italia - Escríbenos o Llámanos'
  };
  
  const seoDescriptions = {
    it: 'Contattaci per informazioni sui tour offroad. WhatsApp: +39 379 379 3030, Email: info@offroaditalia.com',
    en: 'Contact us for offroad tour information. WhatsApp: +39 379 379 3030, Email: info@offroaditalia.com',
    de: 'Kontaktieren Sie uns für Offroad-Tour Informationen. WhatsApp: +39 379 379 3030, Email: info@offroaditalia.com',
    fr: 'Contactez-nous pour des informations sur les tours offroad. WhatsApp: +39 379 379 3030, Email: info@offroaditalia.com',
    es: 'Contáctenos para información de tours offroad. WhatsApp: +39 379 379 3030, Email: info@offroaditalia.com'
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead 
        title={seoTitles[i18n.language] || seoTitles.it}
        description={seoDescriptions[i18n.language] || seoDescriptions.it}
      />
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl font-bold text-white mb-4">{t('contact.title')}</h1>
          <p className="text-xl text-blue-100">{t('contact.subtitle')}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">{i18n.language === 'it' ? 'Inviaci un Messaggio' : i18n.language === 'en' ? 'Send us a Message' : i18n.language === 'de' ? 'Senden Sie uns eine Nachricht' : i18n.language === 'fr' ? 'Envoyez-nous un Message' : 'Envíenos un Mensaje'}</h2>
            
            {submitSuccess && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center" data-testid="success-message">
                <Check className="text-green-600 mr-3" size={24} />
                <span className="text-green-800 font-medium">{t('contact.success')}</span>
              </div>
            )}

            {submitError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <span className="text-red-800">{submitError}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('contact.name')} *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="contact-name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('contact.email')} *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="contact-email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('contact.phone')}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="contact-phone"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('contact.message')} *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="contact-message"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-blue-600 text-white px-6 py-4 rounded-lg hover:bg-blue-700 transition-colors font-semibold text-lg disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
                data-testid="contact-submit-btn"
              >
                {submitting ? (
                  t('contact.sending')
                ) : (
                  <>
                    <Send className="mr-2" size={20} />
                    {t('contact.send')}
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div>
            <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">{i18n.language === 'it' ? 'Informazioni di Contatto' : i18n.language === 'en' ? 'Contact Information' : i18n.language === 'de' ? 'Kontaktinformationen' : i18n.language === 'fr' ? 'Informations de Contact' : 'Información de Contacto'}</h2>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <Phone className="text-blue-600 mr-4 mt-1 flex-shrink-0" size={24} />
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">{t('contact.phone')} / WhatsApp</p>
                    <a 
                      href="https://wa.me/393793793030?text=Ciao%2C%20vorrei%20informazioni%20sugli%20eventi%20Offroad%20Italia" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700 text-lg"
                    >
                      +39 379 379 3030
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <Mail className="text-blue-600 mr-4 mt-1 flex-shrink-0" size={24} />
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Email</p>
                    <a 
                      href="mailto:info@offroaditalia.com"
                      className="text-blue-600 hover:text-blue-700 text-lg"
                    >
                      info@offroaditalia.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <MapPin className="text-blue-600 mr-4 mt-1 flex-shrink-0" size={24} />
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">{t('contact.address')}</p>
                    <p className="text-gray-700">{i18n.language === 'it' ? 'Italia' : 'Italy'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* WhatsApp CTA */}
            <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl shadow-lg p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">{t('contact.whatsapp')}</h3>
              <p className="mb-6 text-green-50">
                {i18n.language === 'it' ? 'Hai bisogno di informazioni immediate? Contattaci direttamente su WhatsApp per una risposta veloce!' : i18n.language === 'en' ? 'Need immediate information? Contact us directly on WhatsApp for a quick response!' : i18n.language === 'de' ? 'Brauchen Sie sofortige Informationen? Kontaktieren Sie uns direkt über WhatsApp für eine schnelle Antwort!' : i18n.language === 'fr' ? 'Besoin d\'informations immédiates? Contactez-nous directement sur WhatsApp pour une réponse rapide!' : '¿Necesita información inmediata? ¡Contáctenos directamente en WhatsApp para una respuesta rápida!'}
              </p>
              <a
                href="https://wa.me/393793793030?text=Ciao%2C%20vorrei%20informazioni%20sugli%20eventi%20Offroad%20Italia"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-white text-green-600 px-6 py-3 rounded-lg hover:bg-gray-100 transition-colors font-semibold"
                data-testid="whatsapp-btn"
              >
                <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                {i18n.language === 'it' ? 'Apri WhatsApp' : i18n.language === 'en' ? 'Open WhatsApp' : i18n.language === 'de' ? 'WhatsApp öffnen' : i18n.language === 'fr' ? 'Ouvrir WhatsApp' : 'Abrir WhatsApp'}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
